import React from 'react';
import { useForm } from 'react-hook-form';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';

import NRSBOMAddModalContentStyles from './NRSBOMAddModalContent.styles';
import NRConfirmationModal from '../../NRConfirmationModal/NRConfirmationModal';
import LicenseDropdown from '../LicenseList/LicenseListDropdown';
import useNRMutation from '../../../graphql/UseNRMutation';
import { QL_ADD_DEPENDENCY, QL_MODIFY_DEPENDENCY } from '../../../graphql/queries/dependency';
import CustomError from '../../CustomError/CustomError';
import NRControlledTextField from '../../NRControlledTextField/NRControlledTextField';
import NRControlledSelect from '../../NRControlledSelect/NRControlledSelect';
import { validateCPE, validatePurl } from '../../../utils/utils';
import { CONSTANTS } from '../../../constants/constants';
import ChipInput from '../../NRControlledChipInput/NRControlledChipInput';

const useStyles = makeStyles(NRSBOMAddModalContentStyles);

const TYPE_OPTIONS = [
  { value: 'APPLICATION', label: 'Application' },
  { value: 'CONTAINER', label: 'Container' },
  { value: 'DEVICE', label: 'Device' },
  { value: 'FRAMEWORK', label: 'Framework' },
  { value: 'KERNEL', label: 'Kernel' },
  { value: 'KERNEL_MODULE', label: 'Kernel Module' },
  { value: 'LIBRARY', label: 'Library' },
  { value: 'OPERATING_SYSTEM', label: 'Operating System' },
  { value: 'PACKAGE', label: 'Package' }
];

export default function NRSBOMAddModalContent({ composedAssetId, onClose, onRefresh, isEditMode = false, initialValues = null }) {
  const classes = useStyles();
  const {
    handleSubmit,
    getValues,
    setValue,
    watch,
    control,
    formState: { errors }
  } = useForm({
    defaultValues:
      isEditMode && initialValues
        ? {
            name: initialValues.name || '',
            vendor: initialValues.vendor || '',
            version: initialValues.version || '',
            type: initialValues.type || '',
            licenses: initialValues.license || [],
            cpes: initialValues.cpes || [],
            purls: initialValues.purls || [],
            identifierInput: '',
            description: initialValues.description || '',
            reason: initialValues.latestRemediation?.reason || ''
          }
        : {
            name: '',
            vendor: '',
            version: '',
            type: '',
            licenses: [],
            cpes: [],
            purls: [],
            identifierInput: '',
            description: '',
            reason: ''
          }
  });

  const licenses = watch('licenses');
  const cpes = watch('cpes');
  const purls = watch('purls');
  const identifierInput = watch('identifierInput');

  const { enqueueSnackbar } = useSnackbar();
  const [addDependency] = useNRMutation(QL_ADD_DEPENDENCY);
  const [modifyDependency] = useNRMutation(QL_MODIFY_DEPENDENCY);

  const successMessage = isEditMode ? 'updated successfully, report is updating' : 'added successfully, report is updating';
  const errorMessage = isEditMode
    ? 'Something went wrong while updating the component, please try again later.'
    : 'Something went wrong while adding the component, please try again later.';

  const handleAddIdentifier = identifier => {
    if (!identifier || !identifier.trim()) return false;

    const trimmed = identifier.trim();
    let validationResult;
    let type;

    if (trimmed.startsWith(CONSTANTS.CPE_PREFIX)) {
      validationResult = validateCPE(trimmed);
      type = 'cpe';
    } else if (trimmed.startsWith(CONSTANTS.PKG_PREFIX)) {
      validationResult = validatePurl(trimmed);
      type = 'purl';
    } else {
      setValue('identifierInputError', 'Identifier must start with "cpe:" or "pkg:"');
      return false;
    }

    if (validationResult !== true) {
      setValue('identifierInputError', validationResult);
      return false;
    }

    const allIdentifiers = [...cpes, ...purls];
    if (allIdentifiers.includes(trimmed)) {
      setValue('identifierInputError', 'This identifier has already been added');
      return false;
    }

    if (type === 'cpe') setValue('cpes', [...cpes, trimmed]);
    if (type === 'purl') setValue('purls', [...purls, trimmed]);

    setValue('identifierInput', '');
    setValue('identifierInputError', '');

    return true;
  };

  const identifierInputError = watch('identifierInputError');

  const onSubmit = data => {
    if (data.identifierInput && data.identifierInput.trim()) {
      const success = handleAddIdentifier(data.identifierInput);
      if (!success) {
        return;
      }
    }

    let finalCpes = Array.from(new Set(data.cpes));
    let finalPurls = Array.from(new Set(data.purls));

    if (data.identifierInput && data.identifierInput.trim()) {
      const value = data.identifierInput.trim();
      const allIdentifiers = [...finalCpes, ...finalPurls];
      if (!allIdentifiers.includes(value)) {
        if (value.startsWith(CONSTANTS.CPE_PREFIX)) {
          finalCpes.push(value);
        } else if (value.startsWith(CONSTANTS.PKG_PREFIX)) {
          finalPurls.push(value);
        }
      }
    }

    const dependencyFields = {
      name: data.name,
      type: data.type,
      vendor: data.vendor,
      version: data.version,
      license: data.licenses,
      cpes: finalCpes,
      purls: finalPurls,
      description: data.description
    };

    const baseArgs = {
      reason: data.reason
    };

    const mutation = isEditMode ? modifyDependency : addDependency;
    const args = isEditMode
      ? {
          ...baseArgs,
          identification: {
            composedAssetId,
            identificationIds: initialValues?.identificationIds || []
          },
          dependencyFields
        }
      : {
          ...baseArgs,
          composedAssetId,
          dependencyFields
        };

    mutation({
      variables: { args },
      context: { version: 'v3' },
      onCompleted: response => {
        const err = isEditMode ? response?.asset?.modifyDependency?.err : response?.asset?.addDependency?.err;

        if (err && err.trim() !== '') {
          enqueueSnackbar(<CustomError message={errorMessage} />, {
            variant: 'error',
            autoHideDuration: null
          });
        } else {
          enqueueSnackbar(`${data.name} ${successMessage}`, { variant: 'success' });
          if (onRefresh) onRefresh();
        }
      },
      onError: () => {
        enqueueSnackbar(<CustomError message={errorMessage} />, {
          variant: 'error',
          autoHideDuration: null
        });
      }
    });

    onClose();
  };

  return (
    <NRConfirmationModal
      title={<Typography variant="h3">{isEditMode ? 'Modify Component' : 'Add Component'}</Typography>}
      content={
        <Box display="flex" flexDirection="column" gap={3} sx={{ overflowY: 'auto', maxHeight: '70vh' }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" gap={2} sx={{ paddingTop: '5px' }}>
              <NRControlledTextField
                className={clsx(classes.fullWidth)}
                label="Name *"
                variant="outlined"
                color="secondary"
                size="small"
                placeholder="Enter the component name..."
                name="name"
                control={control}
                rules={{ required: 'Component name is required' }}
                error={!!errors.name}
                helperText={errors.name?.message}
                required={true}
                defaultValue=""
              />
              <NRControlledTextField
                className={clsx(classes.fullWidth)}
                label="Vendor"
                variant="outlined"
                color="secondary"
                size="small"
                placeholder="Vendor"
                name="vendor"
                control={control}
                defaultValue=""
              />
            </Box>

            <Box display="flex" gap={2}>
              <NRControlledTextField
                className={clsx(classes.fullWidth)}
                label="Version"
                variant="outlined"
                color="secondary"
                size="small"
                placeholder="Version"
                name="version"
                control={control}
                defaultValue=""
              />
              <NRControlledSelect
                className={clsx(classes.fullWidth)}
                size="small"
                id="type"
                name="type"
                label="Type"
                options={TYPE_OPTIONS}
                control={control}
                rules={{ required: 'Component type is required' }}
                error={!!errors.type}
                helperText={errors.type?.message}
                required={true}
                sxProps={{ width: '100%' }}
                showIcon={false}
              />
            </Box>
          </Box>

          {/* Licenses */}
          <Box display="flex" flexDirection="column" gap={2}>
            <LicenseDropdown value={licenses} setValue={newLicenses => setValue('licenses', !!newLicenses ? [newLicenses] : null)} />
          </Box>

          <ChipInput
            name="identifierInput"
            control={control}
            setValue={setValue}
            getValues={getValues}
            values={[...cpes, ...purls]}
            setValues={vals => {
              setValue(
                'cpes',
                vals.filter(v => v.startsWith(CONSTANTS.CPE_PREFIX))
              );
              setValue(
                'purls',
                vals.filter(v => v.startsWith(CONSTANTS.PKG_PREFIX))
              );
            }}
            label="Identifier"
            placeholder="Enter CPE (cpe:...) or pURL (pkg:...), then press Enter"
            error={identifierInputError}
            helperText="Enter a CPE or pURL, multiple entries are allowed."
            helperErrorText={identifierInputError}
            onEnter={handleAddIdentifier}
            onRestore={chip => {
              if (chip.startsWith(CONSTANTS.CPE_PREFIX)) {
                setValue('cpes', [...cpes, chip]);
              } else if (chip.startsWith(CONSTANTS.PKG_PREFIX)) {
                setValue('purls', [...purls, chip]);
              }
            }}
          />

          {/* Description */}
          <NRControlledTextField
            fullWidth
            label="Description"
            variant="outlined"
            color="secondary"
            size="small"
            multiline
            rows={3}
            placeholder="Add an optional description for this component"
            name="description"
            control={control}
            defaultValue=""
          />

          {/* Modification Reason */}
          <NRControlledTextField
            fullWidth
            label="Reason for Modification"
            variant="outlined"
            color="secondary"
            size="small"
            multiline
            rows={3}
            placeholder="Add an optional reason for modification"
            name="reason"
            control={control}
            defaultValue=""
          />
        </Box>
      }
      open={true}
      cancelLabel="Cancel"
      actionLabel={isEditMode ? 'Modify' : 'Add'}
      handleClose={onClose}
      handleCancel={onClose}
      handleActionConfirm={handleSubmit(onSubmit)}
      modalWidth={'600px'}
    />
  );
}
